<template>
  <coozzy-card :data-cy="`digital-reference-questions-${referenceType}`">
    <div class="question-row question-header d-none d-lg-block">
      <div class="row">
        <div class="col-lg-5">
          {{ $t('message.digitalReference.table.question') }}
        </div>
        <div class="col-lg-3">
          {{ $t('message.digitalReference.table.applicantAnswer') }}
        </div>
        <div class="col-lg-4">
          {{ assessmentLabel }}
        </div>
      </div>
    </div>
    <div
      v-for="(question, index) in questions"
      :key="question.key"
      class="question-row"
      :class="{ 'question-row-alt': index % 2 === 1, 'is-invalid-row': showErrors && !isAnswered(question) }">
      <div class="row align-items-center">
        <div class="col-12 col-lg-5 mb-2 mb-lg-0">
          <div class="mobile-column-label d-lg-none">
            {{ $t('message.digitalReference.table.question') }}
          </div>
          <span class="question-label">{{ question.label }}*</span>
        </div>
        <div class="col-12 col-lg-3 mb-2 mb-lg-0">
          <div class="mobile-column-label d-lg-none">
            {{ $t('message.digitalReference.table.applicantAnswer') }}
          </div>
          <span class="applicant-answer">{{ question.applicantValue }}</span>
        </div>
        <div class="col-12 col-lg-4 mb-0">
          <div class="mobile-column-label d-lg-none">
            {{ assessmentLabel }}
          </div>
          <div class="segmented-buttons">
            <button
              v-for="choice in question.choices"
              :key="choice.value"
              type="button"
              class="btn segmented-button"
              :class="{ active: question.value === choice.value }"
              :data-cy="`digital-reference-answer-${question.key}-${choice.value}`"
              @click="$emit('change', { key: question.key, value: choice.value })">
              {{ choice.label }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </coozzy-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import { V2ReferenceConfirmation, V2ReferenceYesNo } from '@/types/api'
import { DigitalReferenceQuestion, DigitalReferenceType } from '@/types/models'

export default Vue.extend({
  name: 'DigitalReferenceQuestionsCard',
  components: {
    CoozzyCard
  },
  props: {
    questions: {
      type: Array as PropType<DigitalReferenceQuestion[]>,
      default: () => []
    },
    showErrors: {
      type: Boolean,
      default: false
    },
    referenceType: {
      type: String as PropType<DigitalReferenceType>,
      required: true
    }
  },
  computed: {
    assessmentLabel(): string {
      const key = this.referenceType === 'employer'
        ? 'message.digitalReference.table.employerAssessment'
        : 'message.digitalReference.table.landlordAssessment'
      return this.$t(key) as string
    }
  },
  methods: {
    isAnswered(question: DigitalReferenceQuestion): boolean {
      return question.value !== V2ReferenceConfirmation.REFERENCE_CONFIRMATION_UNDEFINED &&
        question.value !== V2ReferenceYesNo.REFERENCE_YES_NO_UNDEFINED
    }
  }
})
</script>

<style lang="scss" scoped>
.question-row {
  padding: 16px 8px;
  border-bottom: 1px solid $color-bg-grey-dark;

  &:last-child {
    border-bottom: none;
  }

  &.question-row-alt {
    background-color: rgba(0, 0, 0, 0.02);
  }

  &.is-invalid-row {
    outline: 1px solid $color-red;
    outline-offset: -1px;
  }
}

.question-header {
  font-weight: 600;
  color: $color-text-grey-dark;
  border-bottom: 1px solid $color-bg-grey-dark;
}

.mobile-column-label {
  font-weight: 600;
  font-size: 12px;
  color: $color-text-grey-dark;
  margin-bottom: 6px;
}

.question-label {
  font-weight: 500;
}

.applicant-answer {
  color: $color-text-grey-dark;
}

.segmented-buttons {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
}

.segmented-button {
  background-color: $color-bg-grey-dark;
  border: 1px solid $color-bg-grey-dark;
  color: $color-text-grey-dark;
  flex: 1 1 0;
  min-width: 0;
  white-space: normal;
  line-height: 1.2;
  font-size: 12px;
  padding: 6px 4px;

  &.active {
    background-color: $color-primary;
    border-color: $color-primary;
    color: $color-text-white;
  }
}

@media (max-width: 991px) {
  .segmented-buttons {
    flex-wrap: wrap;
  }

  .segmented-button {
    flex: 1 1 auto;
    min-width: 95px;
  }

  .applicant-answer {
    display: block;
    margin-top: 4px;
  }
}
</style>
